import React, { Suspense, lazy } from 'react';
import { Loader2 } from 'lucide-react';
import { getThemeClass } from '../../../utils/theme';
import useAdminPanel from './hooks/useAdminPanel';
import AdminHeader from './AdminHeader';
import AdminSidebar from './AdminSidebar';
import { NAV_ITEMS } from './config';

// Lazy load dashboard components for better performance
const PluginDashboardContent = lazy(() => 
  import('../PluginDashboard').then(m => ({ default: m.PluginDashboardContent }))
);
const CredentialDashboardContent = lazy(() => 
  import('../CredentialDashboard').then(m => ({ default: m.CredentialDashboardContent }))
);
const PermissionsDashboardContent = lazy(() => 
  import('../PermissionsDashboard').then(m => ({ default: m.PermissionsDashboardContent }))
);
const SpaceDashboardContent = lazy(() => 
  import('../SpaceDashboard').then(m => ({ default: m.SpaceDashboardContent }))
);
const LauncherDashboardContent = lazy(() => 
  import('../LauncherDashboard').then(m => ({ default: m.LauncherDashboardContent }))
);
const EnvironmentDashboardContent = lazy(() => 
  import('../EnvironmentDashboard').then(m => ({ default: m.EnvironmentDashboardContent }))
);
const PageEditorContent = lazy(() => 
  import('../PageEditor').then(m => ({ default: m.PageEditorContent }))
);
const BlogEditorContent = lazy(() => 
  import('../BlogEditor').then(m => ({ default: m.BlogEditorContent }))
);
const RedirectEditorContent = lazy(() => 
  import('../RedirectEditor').then(m => ({ default: m.RedirectEditorContent }))
);
const GlobalStateDebug = lazy(() => import('./GlobalStateDebug'));
const PackageStoreContent = lazy(() => 
  import('../PackageStore').then(m => ({ default: m.PackageStoreContent }))
);
const ApplicationManagerContent = lazy(() => 
  import('../ApplicationManager').then(m => ({ default: m.ApplicationManagerContent }))
);
const InstalledPackagesManager = lazy(() => 
  import('../InstalledPackages').then(m => ({ default: m.InstalledPackagesManager }))
);
const SDKMenuContent = lazy(() => 
  import('../SDKMenu').then(m => ({ default: m.SDKMenuContent }))
);

/**
 * Map section IDs to their dashboard components
 */
const dashboardComponents = {
  plugins: PluginDashboardContent,
  credentials: CredentialDashboardContent,
  permissions: PermissionsDashboardContent,
  spaces: SpaceDashboardContent,
  launchers: LauncherDashboardContent,
  applications: ApplicationManagerContent,
  environments: EnvironmentDashboardContent,
  pages: PageEditorContent,
  blog: BlogEditorContent,
  redirects: RedirectEditorContent,
  debug: GlobalStateDebug,
  packages: PackageStoreContent,
  installed: InstalledPackagesManager,
  sdk: SDKMenuContent
};

/**
 * Loading fallback for lazy-loaded dashboards
 */
const DashboardLoader = ({ theme }) => (
  <div className="h-full flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <Loader2 size={32} className="animate-spin text-blue-400" />
      <p className={getThemeClass(theme, 'text.secondary')}>Loading...</p>
    </div>
  </div>
);

/**
 * AdminPanel - Unified admin settings panel
 * 
 * A full-screen modal with sidebar navigation and content area.
 * All existing dashboards render in the content area based on sidebar selection.
 * 
 * @param {Object} props
 * @param {Function} props.onClose - Callback to close the admin panel
 * @param {string} props.theme - Theme ('dark' or 'light')
 * @param {Function} props.onLaunchSDK - Optional callback to launch SDK (for window system integration)
 */
const AdminPanel = ({ onClose, theme = 'dark', onLaunchSDK }) => {
  const {
    activeSection,
    setActiveSection,
    isMobileNavOpen,
    toggleMobileNav,
    closeMobileNav
  } = useAdminPanel({ onClose });

  // Get the component for the active section
  const ActiveDashboard = dashboardComponents[activeSection];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-panel-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Panel Container */}
      <div 
        className={`
          relative flex flex-col
          w-full h-full
          lg:w-[90vw] lg:h-[90vh] lg:max-w-7xl lg:max-h-[900px]
          lg:rounded-xl lg:shadow-2xl
          overflow-hidden
          ${getThemeClass(theme, 'panel')}
          ${getThemeClass(theme, 'border')}
        `}
      >
        {/* Header */}
        <AdminHeader
          onClose={onClose}
          onToggleMobileNav={toggleMobileNav}
          isMobileNavOpen={isMobileNavOpen}
          theme={theme}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex min-h-0">
          {/* Desktop Sidebar - always visible on lg+ */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <AdminSidebar
              activeSection={activeSection}
              onNavigate={setActiveSection}
              theme={theme}
            />
          </div>

          {/* Mobile Sidebar - slide-out drawer */}
          {isMobileNavOpen && (
            <>
              {/* Mobile backdrop */}
              <div 
                className="lg:hidden fixed inset-0 bg-black/50 z-40"
                onClick={closeMobileNav}
                aria-hidden="true"
              />
              {/* Mobile drawer */}
              <div 
                className={`
                  lg:hidden fixed left-0 top-0 bottom-0 w-64 z-50
                  transform transition-transform duration-200
                  ${getThemeClass(theme, 'panel')}
                  shadow-xl
                `}
              >
                <AdminSidebar
                  activeSection={activeSection}
                  onNavigate={setActiveSection}
                  theme={theme}
                  className="pt-14"
                />
              </div>
            </>
          )}

          {/* Content Area - Render Active Dashboard */}
          <main className="flex-1 min-w-0 overflow-hidden">
            {ActiveDashboard ? (
              <Suspense fallback={<DashboardLoader theme={theme} />}>
                <ActiveDashboard 
                  theme={theme}
                  onLaunchSDK={onLaunchSDK}
                  onClose={onClose}
                />
              </Suspense>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className={getThemeClass(theme, 'text.secondary')}>
                  Select a section from the sidebar
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
